import React, { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { collection, doc, getDoc, limit, onSnapshot, query, where } from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";
import { db } from "./firebaseConfig";
import Navbar from "./components/Navbar";
import FormBuilder from "./pages/FormBuilder";
import FormFill from "./pages/FormFill";
import IssueBoard from "./pages/IssueBoard";
import MailSchedulerControl from "./pages/MailSchedulerControl";

const extractPerms = (obj = {}) => {
  if (!obj || typeof obj !== "object") return null;
  const map = obj["kontrol-formlari"] || obj.kontrolFormlari || obj.kontrol_formlari || null;
  return map && Object.keys(map).length > 0 ? map : null;
};

const isCreatorRole = (role = "") => {
  const text = String(role).toLocaleLowerCase("tr-TR");
  return ["creator", "olusturucu", "oluşturucu"].includes(text);
};

const hasAny = (obj, keys) => keys.some((key) => obj?.[key] === true);

const Guard = ({ allow, children }) =>
  allow ? children : <div style={{ padding: 16 }}>Bu sayfayı görüntüleme yetkiniz yok.</div>;

export default function Main() {
  const { user, role, loading, userPermissions } = useAuth();
  const [ready, setReady] = useState(false);
  const [perms, setPerms] = useState(null);

  useEffect(() => {
    const fromCtx = extractPerms(userPermissions || {});
    if (fromCtx) {
      setPerms(fromCtx);
      setReady(true);
      return undefined;
    }
    if (!user?.email) {
      setReady(true);
      return undefined;
    }

    let stopped = false;
    let unsub = null;

    const loadPerms = async () => {
      const direct = await getDoc(doc(db, "users", user.email));
      if (stopped) return;
      const directPerms = direct.exists() ? extractPerms(direct.data() || {}) : null;
      if (directPerms) {
        setPerms(directPerms);
        setReady(true);
        return;
      }

      const qRef = query(collection(db, "users"), where("email", "==", user.email), limit(1));
      unsub = onSnapshot(qRef, (snap) => {
        let found = null;
        snap.forEach((d) => {
          found = extractPerms(d.data() || {});
        });
        setPerms(found || {});
        setReady(true);
      }, () => setReady(true));
    };

    loadPerms();

    return () => {
      stopped = true;
      if (unsub) unsub();
    };
  }, [user?.email, userPermissions]);

  if (loading || !ready) {
    return (
      <div>
        <Navbar perms={{}} />
        <div style={{ padding: 16 }}>Yükleniyor...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;

  const creator = isCreatorRole(role);
  const canCreate = creator || hasAny(perms, ["olusturur", "oluşturur", "yonetici"]);
  const canFill = creator || canCreate || hasAny(perms, ["doldurur", "doldur"]);
  const canBackfillDate = creator || canCreate || hasAny(perms, ["gecmisTarihDoldurur", "geçmişTarihDoldurur"]);
  const canResolveAll = creator || canCreate || hasAny(perms, ["cozer", "çözer", "tumunuCozer", "tümünüÇözer"]);
  const canResolveOwn = canResolveAll || hasAny(perms, ["sadeceKendiEksikleriniCozer", "sadeceKendiEksikleriniÇözer", "kendiEksiginiCozer"]);
  const canViewIssues = creator || canCreate || canResolveAll || canResolveOwn || hasAny(perms, ["gorur", "görür", "eksiklikleriGorur"]);
  const canMail = creator || canCreate || hasAny(perms, ["mailAyarlar", "mailAyarlari", "mailGonderir"]);
  const canWriteNewTargets = creator || canCreate || hasAny(perms, ["yeniHedefYazar", "yeniSantiyeTesisYazar", "serbestHedefYazar"]);
  const canViewModule = canCreate || canFill || canViewIssues || canMail;

  if (!canViewModule) {
    return (
      <div>
        <Navbar perms={{}} />
        <div style={{ padding: 16 }}>Bu projeyi görüntüleme yetkiniz yok.</div>
      </div>
    );
  }

  const navPerms = { canCreate, canFill, canViewIssues, canMail };
  const defaultPath = canFill ? "form-doldur" : canViewIssues ? "eksiklikler" : canMail ? "mail-ayarlari" : "formlar";

  return (
    <div>
      <Navbar perms={navPerms} />
      <main style={{ padding: 12 }}>
        <Routes>
          <Route index element={<Navigate to={defaultPath} replace />} />
          <Route path="formlar" element={<Guard allow={canCreate}><FormBuilder /></Guard>} />
          <Route path="form-doldur" element={<Guard allow={canFill}><FormFill canBackfillDate={canBackfillDate} allowedPlates={perms?.allowedPlates || perms?.izinliPlakalar || []} canWriteNewTargets={canWriteNewTargets} /></Guard>} />
          <Route path="eksiklikler" element={<Guard allow={canViewIssues}><IssueBoard canResolveAll={canResolveAll} canResolveOwn={canResolveOwn} /></Guard>} />
          <Route path="mail-ayarlari" element={<Guard allow={canMail}><MailSchedulerControl /></Guard>} />
          <Route path="*" element={<Navigate to={defaultPath} replace />} />
        </Routes>
      </main>
    </div>
  );
}
